import { useState, useCallback } from 'react';
import { JourneyApiService } from '../services/journeyApiService';
import { clearJourneyCache } from '../components/journey/JourneyHomeSection';
import {
  Journey,
  JourneyStep,
  JourneySetup,
  UpdateStepStatusRequest,
  CreateJourneyResponse
} from '../types/journey';

/**
 * React hook for journey management API operations
 * Provides state management and error handling for journey operations
 */
export function useJourneyApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeJourney, setActiveJourney] = useState<{
    journey: Journey;
    steps: JourneyStep[];
  } | null>(null);

  /**
   * Clear any existing error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Create a new journey
   */
  const createJourney = useCallback(async (setup: JourneySetup): Promise<CreateJourneyResponse | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await JourneyApiService.createJourney({ setup });
      setActiveJourney(result);
      
      // Clear journey cache since we have a new journey
      clearJourneyCache();
      
      return result;
    } catch (err: any) {
      setError(err.message || 'Failed to create journey');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get user's active journey
   */
  const getActiveJourney = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await JourneyApiService.getUserActiveJourney();
      setActiveJourney(result);
      return result !== null;
    } catch (err: any) {
      setError(err.message || 'Failed to get active journey');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get a specific journey by ID
   */
  const getJourney = useCallback(async (journeyId: string): Promise<{
    journey: Journey;
    steps: JourneyStep[];
  } | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await JourneyApiService.getJourney(journeyId);
      return result;
    } catch (err: any) {
      setError(err.message || 'Failed to get journey');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update step status (complete or skip)
   */
  const updateStepStatus = useCallback(async (request: UpdateStepStatusRequest): Promise<{
    step: JourneyStep;
    pointsAwarded: number;
    nextStepUnlocked?: boolean;
  } | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await JourneyApiService.updateStepStatus(request);
      
      // Clear journey cache to ensure fresh data on next load
      clearJourneyCache();
      
      // Update local state if this is the active journey
      if (activeJourney) {
        const updatedSteps = activeJourney.steps.map(step => 
          step.id === result.step.id ? result.step : step
        );
        
        // If next step was unlocked, update its status too
        if (result.nextStepUnlocked) {
          const nextStepIndex = result.step.stepIndex + 1;
          const nextStepId = activeJourney.steps.find(s => s.stepIndex === nextStepIndex)?.id;
          if (nextStepId) {
            const updatedStepsWithNext = updatedSteps.map(step =>
              step.id === nextStepId ? { ...step, status: 'current' as const } : step
            );
            setActiveJourney({
              ...activeJourney,
              journey: {
                ...activeJourney.journey,
                currentStepIndex: nextStepIndex
              },
              steps: updatedStepsWithNext
            });
          }
        } else {
          setActiveJourney({
            ...activeJourney,
            steps: updatedSteps
          });
        }
      }
      
      return result;
    } catch (err: any) {
      setError(err.message || 'Failed to update step status');
      return null;
    } finally {
      setLoading(false);
    }
  }, [activeJourney]);

  /**
   * Complete a step
   */
  const completeStep = useCallback(async (stepId: string, reflectionNotes?: string) => {
    return updateStepStatus({
      stepId,
      status: 'completed',
      reflectionNotes
    });
  }, [updateStepStatus]);

  /**
   * Skip a step
   */
  const skipStep = useCallback(async (stepId: string, justification?: string) => {
    return updateStepStatus({
      stepId,
      status: 'skipped',
      reflectionNotes: justification
    });
  }, [updateStepStatus]);

  /**
   * Reset the current journey
   */
  const resetJourney = useCallback(async (newSetup?: JourneySetup): Promise<CreateJourneyResponse | null> => {
    if (!activeJourney) {
      setError('No active journey to reset');
      return null;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await JourneyApiService.resetJourney({
        journeyId: activeJourney.journey.id,
        newSetup
      });
      setActiveJourney(result);
      
      // Clear journey cache since we have a reset journey
      clearJourneyCache();
      
      // Dispatch custom event to notify other components (like TopNavbar) about the reset
      window.dispatchEvent(new CustomEvent('journeyReset', { 
        detail: { newJourneyId: result.journey.id } 
      }));
      
      return result;
    } catch (err: any) {
      setError(err.message || 'Failed to reset journey');
      return null;
    } finally {
      setLoading(false);
    }
  }, [activeJourney]);

  /**
   * Get current step from active journey
   */
  const getCurrentStep = useCallback((): JourneyStep | null => {
    if (!activeJourney) return null;
    
    return activeJourney.steps.find(step => step.status === 'current') || null;
  }, [activeJourney]);

  /**
   * Get completed steps count
   */
  const getCompletedStepsCount = useCallback((): number => {
    if (!activeJourney) return 0;
    
    return activeJourney.steps.filter(step => step.status === 'completed').length;
  }, [activeJourney]);

  /**
   * Get total steps count
   */
  const getTotalStepsCount = useCallback((): number => {
    if (!activeJourney) return 0;
    
    return activeJourney.steps.length;
  }, [activeJourney]);

  /**
   * Calculate progress percentage
   */
  const getProgressPercentage = useCallback((): number => {
    const completed = getCompletedStepsCount();
    const total = getTotalStepsCount();
    
    if (total === 0) return 0;
    
    return Math.round((completed / total) * 100);
  }, [getCompletedStepsCount, getTotalStepsCount]);

  return {
    // State
    loading,
    error,
    activeJourney,
    
    // Actions
    createJourney,
    getActiveJourney,
    getJourney,
    updateStepStatus,
    completeStep,
    skipStep,
    resetJourney,
    clearError,
    
    // Computed values
    getCurrentStep,
    getCompletedStepsCount,
    getTotalStepsCount,
    getProgressPercentage,
    
    // Convenience getters
    hasActiveJourney: activeJourney !== null,
    currentStep: getCurrentStep(),
    progressPercentage: getProgressPercentage(),
    completedSteps: getCompletedStepsCount(),
    totalSteps: getTotalStepsCount()
  };
}